import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';

import { useAuth } from '../../context/Auth';
import { useModalContext } from '../../context/Modal';

import {
  DELETE_ADDITIONAL_PROFILE,
  UPDATE_ADDITIONAL_PROFILE,
} from '../../http/apollo-queries';

import { Button, Card, Icon } from '../../components/UI';
import { Input } from '../../components/form';
import { ConfirmAction } from '../../components/Modal';
import { IProfile } from '../../types/model';

type SecondaryProfileProps = {
  profile: IProfile;
  index: number;
};

export const SecondaryProfile = ({ profile, index }: SecondaryProfileProps) => {
  const [updateAdditionProfile, { loading }] = useMutation(
    UPDATE_ADDITIONAL_PROFILE
  );
  const { t } = useTranslation();
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const { setElement } = useModalContext();

  const [avatarHovered, setAvatarHovered] = useState(false);

  const [deleteAdditionalProfile, { loading: loadingDeleteProfile }] =
    useMutation(DELETE_ADDITIONAL_PROFILE, {
      variables: {
        profileId: profile.id,
      },
      onCompleted: () => refetch(),
    });

  const handleDeleteProfile = () => {
    const confirmedTitle = t('modals.deleteSecondaryProfile.confirmedTitle');
    const confirmedSubtitle = t(
      'modals.deleteSecondaryProfile.confirmedSubtitle'
    );
    const title = t('modals.deleteSecondaryProfile.title');
    const subtitle = t('modals.deleteSecondaryProfile.subtitle');

    setElement(
      <ConfirmAction
        confirmedSubtitle={confirmedSubtitle}
        confirmedTitle={confirmedTitle}
        loading={loadingDeleteProfile}
        save={deleteAdditionalProfile}
        subtitle={subtitle}
        title={title}
      />
    );
  };

  const formikForSecondaryProfile = useFormik({
    initialValues: {
      username: profile.name,
    },
    onSubmit: async (values) => {
      updateAdditionProfile({
        variables: {
          username: values.username,
          profileId: profile.id,
        },
      }).then((res) => {
        toast.success(t('account.mainProfile.form.success.saveUsername'));
      });
    },
    validateOnMount: true,
  });

  return (
    <Card className="mt-4" title={t('account.profiles.cardTitle')}>
      <FormikProvider value={formikForSecondaryProfile}>
        <form onSubmit={formikForSecondaryProfile.handleSubmit}>
          <div className="flex flex-col md:flex-row justify-start align-center">
            <div>
              <div
                className="relative rounded-full mx-auto md:mr-10 w-32 h-32 bg-grey-10"
                onMouseEnter={() => {
                  setAvatarHovered(true);
                }}
                onMouseLeave={() => {
                  setAvatarHovered(false);
                }}
              >
                {typeof profile?.avatar?.size?.avatarLarge === 'string' && (
                  <img
                    className="rounded-full w-32 h-32"
                    src={profile.avatar?.size.avatarLarge}
                    alt="profile-icon"
                  />
                )}
                <div
                  className={
                    'bg-black bg-opacity-50 rounded-full flex items-center cursor-pointer justify-center top-0 left-0 absolute w-full h-full ' +
                    (avatarHovered ? '' : 'hidden')
                  }
                  onClick={() => {
                    //setUploadImageStep(UploadImageEnum.DROPZONE)
                    navigate('/upload-image?profileId=' + profile.id);
                  }}
                >
                  <Icon
                    className="h-12 text-white pointer-events-none"
                    color="black"
                    svgName={'circlePencil'}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-5 w-full">
              <div className="flex gap-10">
                <Input
                  size="medium"
                  name="username"
                  id={'secondary-account' + index}
                  formik={formikForSecondaryProfile}
                  type="text"
                  label={t('account.profiles.name')}
                />
              </div>
              <div className="flex justify-end gap-5">
                <Button
                  loading={loading}
                  type="submit"
                  buttonClass="bg-primary w-40 h-10 font-semibold"
                >
                  {t('account.profiles.save')}
                </Button>
                <Button
                  type="button"
                  buttonClass="border border-negative text-negative w-40 h-10 font-semibold"
                  loading={loadingDeleteProfile}
                  onClick={handleDeleteProfile}
                >
                  {t('account.profiles.delete')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormikProvider>
    </Card>
  );
};
